import { z } from 'zod'

import { RecordSourceType } from '../enums'

import { pageSchema } from './page'
import { trashPageMetaSchema } from './trash'

export const recordValueSchema = z.object({
  valueId: z.string().uuid(),
  formFieldId: z.string().uuid(),
  // Stable cross-form-version identifier — survives form re-saves as long as the
  // field is carried forward unmodified. Prefer this over formFieldLabel when
  // matching a value to a column, since labels are free text and are not
  // guaranteed unique within a form.
  formFieldKey: z.string().nullable(),
  // The backend always sends formFieldLabel; it can be null if the field was deleted.
  formFieldLabel: z.string().nullable(),
  value: z.string(),
})

export const recordSchema = z.object({
  recordId: z.string().uuid(),
  formId: z.string().uuid(),
  title: z.string().nullable().optional(),
  values: z.array(recordValueSchema),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  deleted: z.boolean().optional(),
  deletedAt: z.string().nullable().optional(),
  sourceType: z.nativeEnum(RecordSourceType).optional(),
  creatorUserId: z.string().uuid().nullable().optional(),
  creatorName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
})

export const recordDetailSchema = z.object({
  recordId: z.string().uuid(),
  formId: z.string().uuid(),
  title: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  sourceType: z.nativeEnum(RecordSourceType).optional(),
  creatorUserId: z.string().uuid().nullable().optional(),
  creatorName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
  fieldsAndValues: z.array(
    z.object({
      field: z.object({
        id: z.string().uuid(),
        formId: z.string().uuid(),
        key: z.string(),
        label: z.string(),
        type: z.string(),
        required: z.boolean(),
        options: z.record(z.unknown()).nullable(),
        validations: z.record(z.unknown()).nullable(),
      }),
      // Null when the field has never been filled in for this record.
      valueId: z.string().uuid().nullable(),
      value: z.string().nullable().transform((v) => v ?? ''),
      // False when field access makes the field read-only for the signed-in user.
      editable: z.boolean().optional().default(true),
      // BUTTON fields only: the latest click on this record, null when never clicked.
      lastClick: z.object({ at: z.string(), by: z.string() }).nullable().optional(),
    })
  ),
  // Phase 5 — record tags assigned to this record
  tags: z.array(
    z.object({
      tagId: z.string().uuid(),
      label: z.string(),
      colorHex: z.string().nullable(),
    })
  ).optional().default([]),
})

export const createRecordRequestSchema = z.object({
  formId: z.string().uuid(),
  values: z.array(
    z.object({
      formFieldId: z.string().uuid(),
      value: z.string(),
    })
  ),
})

// Backend returns DTORecordCreationResponse { formId, recordId } — not a full record.
export const createRecordResponseSchema = z.object({
  formId: z.string().uuid(),
  recordId: z.string().uuid(),
})

// Identify the value by valueId, or by recordId + formFieldId for a field that has no value yet
// (the backend then creates it).
export const updateRecordValueRequestSchema = z
  .object({
    fieldKey: z.string(),
    fieldLabel: z.string(),
    valueId: z.string().uuid().nullable().optional(),
    recordId: z.string().uuid().optional(),
    formFieldId: z.string().uuid().optional(),
    value: z.string(),
  })
  .refine((d) => !!d.valueId || (!!d.recordId && !!d.formFieldId), {
    message: 'valueId or recordId + formFieldId is required',
  })

export const updateRecordValueResponseSchema = z.object({
  message: z.string(),
  valueId: z.string().uuid().nullable().optional(),
})

export const updateRecordRequestSchema = z.object({
  values: z.array(
    z.object({
      formFieldId: z.string().uuid(),
      value: z.string(),
    })
  ),
})

// Trash-specific schema matching DTOTrashRecordResponse from the backend.
// The backend uses 'id' (not 'recordId'), and omits 'values'.
export const trashedRecordItemSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  recordGroupId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  deletedAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const trashedRecordPageSchema = trashPageMetaSchema.extend({
  content: z.array(trashedRecordItemSchema),
})

export type RecordValue = z.infer<typeof recordValueSchema>
export type Record = z.infer<typeof recordSchema>
export type RecordDetail = z.infer<typeof recordDetailSchema>

export const buttonClickResponseSchema = z.object({
  automationsRun: z.number(),
  clickedAt: z.string(),
  clickedBy: z.string(),
})
export type ButtonClickResponse = z.infer<typeof buttonClickResponseSchema>
export type CreateRecordRequest = z.infer<typeof createRecordRequestSchema>
export type CreateRecordResponse = z.infer<typeof createRecordResponseSchema>
export type UpdateRecordValueRequest = z.infer<typeof updateRecordValueRequestSchema>
export type UpdateRecordValueResponse = z.infer<typeof updateRecordValueResponseSchema>
export type UpdateRecordRequest = z.infer<typeof updateRecordRequestSchema>
export type TrashedRecordItem = z.infer<typeof trashedRecordItemSchema>
export type TrashedRecordPage = z.infer<typeof trashedRecordPageSchema>

export interface RecordCreatorInfo {
  sourceType?: RecordSourceType
  creatorUserId?: string | null
  creatorName?: string | null
  creatorEmail?: string | null
}


// ── Paged record search ──────────────────────────────────────────────────────

export const recordTagRefSchema = z.object({
  tagId: z.string().uuid(),
  label: z.string(),
  colorHex: z.string().nullable(),
})

/** A row of the paged records table (mirrors DTORecordListItem). */
export const recordListItemSchema = z.object({
  recordId: z.string().uuid(),
  recordGroupId: z.string().uuid(),
  recordGroupName: z.string(),
  formId: z.string().uuid(),
  title: z.string().nullable(),
  values: z.array(recordValueSchema),
  tags: z.array(recordTagRefSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
  sourceType: z.nativeEnum(RecordSourceType),
  creatorUserId: z.string().uuid().nullable(),
  creatorName: z.string().nullable(),
  creatorEmail: z.string().nullable(),
})

export const recordListPageSchema = pageSchema(recordListItemSchema)

export const recordSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  bySource: z.record(z.nativeEnum(RecordSourceType), z.number().int().nonnegative()),
  tagged: z.number().int().nonnegative(),
  createdLast30Days: z.number().int().nonnegative(),
  childGroups: z.number().int().nonnegative(),
})

export type RecordFieldOperator =
  | 'EQ' | 'NEQ' | 'CONTAINS' | 'NOT_CONTAINS' | 'STARTS_WITH'
  | 'GT' | 'GTE' | 'LT' | 'LTE' | 'BETWEEN'
  | 'IN' | 'IS_EMPTY' | 'IS_NOT_EMPTY'

export type RecordTagMode = 'ANY' | 'ALL' | 'NONE'
export type RecordFilterMatch = 'ALL' | 'ANY'

/** Body of POST /records/record-group/{id}/search (mirrors DTORecordSearchRequest). */
export interface RecordSearchRequest {
  q?: string
  includeSubgroups?: boolean
  match?: RecordFilterMatch
  sources?: RecordSourceType[]
  createdFrom?: string
  createdTo?: string
  updatedFrom?: string
  updatedTo?: string
  creatorUserIds?: string[]
  tags?: { ids: string[]; mode: RecordTagMode }
  fields?: { fieldKey: string; op: RecordFieldOperator; value?: string; values?: string[] }[]
  sort?: { by: string; dir: 'ASC' | 'DESC' }
  page: number
  size: number
  /** Field keys whose values the rows should carry (spreadsheet-style views). */
  columns?: string[]
}

export type RecordTagRef = z.infer<typeof recordTagRefSchema>
export type RecordListItem = z.infer<typeof recordListItemSchema>
export type RecordListPage = z.infer<typeof recordListPageSchema>
export type RecordSummary = z.infer<typeof recordSummarySchema>
